import React, { useState } from "react";
import { Box } from "@mui/material";
import { ITEM_TYPES } from "../../constants/itemTypes";
import CheckboxItem from "./CheckboxItem";
import DateTextItem from "./DateTextItem";
import SelectItem from "./SelectItem";
import NumericItem from "./NumericItem";
import TextareaItem from "./TextareaItem";
import DateItem from "./DateItem";

/**
 * A universal component for rendering different types of items with drag and drop functionality
 */
const ItemRenderer = ({
  item,
  onUpdate,
  onDelete,
  editable = true,
  draggable = true,
  isTemplateView = false,
  onReorder,
  index,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  if (!item) {
    return null;
  }

  const commonProps = {
    id: item.id,
    onUpdate,
    onDelete,
    editable,
    draggable,
    isTemplateView,
  };

  // Drag and drop handlers
  const handleDragStart = (e) => {
    if (!draggable || !onReorder) return;

    setIsDragging(true);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index.toString());

    // Add some visual feedback
    e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    if (!draggable || !onReorder) return;

    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";

    // Only set dragOver if we're not already in that state
    if (!dragOver) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    if (!draggable || !onReorder) return;

    // Only clear dragOver if we're actually leaving the element
    // Check if the related target is not a child of the current element
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOver(false);
    }
  };

  const handleDragEnter = (e) => {
    if (!draggable || !onReorder) return;

    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    if (!draggable || !onReorder) return;

    e.preventDefault();
    setDragOver(false);

    const dragIndex = parseInt(e.dataTransfer.getData("text/plain"));
    const dropIndex = index;

    if (dragIndex !== dropIndex) {
      onReorder(dragIndex, dropIndex);
    }
  };

  const renderItem = () => {
    switch (item.type) {
      case ITEM_TYPES.CHECKBOX:
        return (
          <CheckboxItem
            {...commonProps}
            description={item.description}
            completed={item.completed}
          />
        );

      case ITEM_TYPES.DATE_TEXT:
        return (
          <DateTextItem
            {...commonProps}
            description={item.description}
            date={item.date}
            textValue={item.textValue}
            completed={item.completed}
          />
        );

      case ITEM_TYPES.SELECT:
        return (
          <SelectItem
            {...commonProps}
            description={item.description}
            date={item.date}
            textValue={item.textValue}
            config={item.config || {}}
            completed={item.completed}
          />
        );
      case ITEM_TYPES.NUMBER:
        return (
          <NumericItem
            {...commonProps}
            description={item.description}
            date={item.date}
            textValue={item.textValue}
            config={item.config || {}}
            completed={item.completed}
          />
        );
      case ITEM_TYPES.DATE:
        return (
          <DateItem
            {...commonProps}
            description={item.description}
            date={item.date}
            textValue={item.textValue}
            completed={item.completed}
          />
        );
      case ITEM_TYPES.TEXT_MULTI:
        return (
          <TextareaItem
            {...commonProps}
            description={item.description}
            date={item.date}
            textValue={item.textValue}
            completed={item.completed}
          />
        );

      default:
        // Fallback to checkbox for unknown types or legacy items
        return (
          <CheckboxItem
            {...commonProps}
            description={item.description || "Unknown item type"}
            completed={item.completed}
          />
        );
    }
  };

  // If draggable is false or we're not in template view, just render the item
  if (!draggable || !isTemplateView || !onReorder) {
    return renderItem();
  }

  // Wrap with drag and drop functionality
  return (
    <Box
      draggable={true}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        opacity: isDragging ? 0.5 : 1,
        transform: dragOver ? "scale(1.02)" : "scale(1)",
        transition: "all 0.2s ease-in-out",
        cursor: draggable ? "grab" : "default",
        "&:active": {
          cursor: draggable ? "grabbing" : "default",
        },
        position: "relative",
        "&::before": dragOver
          ? {
              content: '""',
              position: "absolute",
              top: -2,
              left: 0,
              right: 0,
              height: 4,
              backgroundColor: "primary.main",
              borderRadius: 1,
              zIndex: 1,
            }
          : {},
      }}
    >
      {renderItem()}
    </Box>
  );
};

export default ItemRenderer;
